import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ErrorNote, Spinner } from "./primitives";

/**
 * Renders a .docx in the browser, close to how Word lays it out.
 *
 * The library is loaded on demand: it is the heaviest thing the SPA depends on
 * and most sessions never open this tab. Its own stylesheet is scoped to the
 * container it renders into, so it cannot reach the rest of the app.
 */
export function DocxPreview({ runId, name }: { runId: string; name: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");

    (async () => {
      try {
        const [{ renderAsync }, bytes] = await Promise.all([
          import("docx-preview"),
          api.fileBytes(runId, name),
        ]);
        if (cancelled || !host.current) return;
        host.current.innerHTML = "";
        await renderAsync(bytes, host.current, undefined, {
          className: "docx",
          inWrapper: true,
          // **The page keeps its dimensions.** These were `ignoreWidth` and
          // `ignoreHeight: true`, which discards the section geometry and
          // flows the document to fill whatever box it is in — while the
          // stylesheet next door paints a white page and is headed "the .docx
          // preview is paper". Half a metaphor is worse than either whole one:
          // the preview drew paper and then refused to be page-shaped, so
          // margins, column widths and page breaks were all somebody else's.
          //
          // A page wider than the panel scrolls, which `.docx-host` already
          // allows. That is what paper does.
          ignoreWidth: false,
          ignoreHeight: false,
          // Tab stops and a few other layout features the builder relies on —
          // `wordstyle` re-cuts the header and footer stops for this page size,
          // and without this they are ignored.
          experimental: true,
        });
        if (!cancelled) setState("ready");
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setState("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId, name]);

  return (
    <div className="px-5 py-5">
      {state === "loading" ? <Spinner /> : null}
      {state === "error" ? <ErrorNote error={error} /> : null}
      <div
        ref={host}
        className="docx-host overflow-x-auto"
        style={{ display: state === "ready" ? "block" : "none" }}
      />
    </div>
  );
}
